import {IAngularEvent, ILogService, IScope} from "angular";
import {regNavn} from "felles/tjenester/tredjepartsTjenestenavn";
import {Debugger} from "debug";
import _ from "lodash";
import {Subject} from "rxjs";

const debug: Debugger = require('debug')('PdfjsMeldingshåndtering');

/**
 * Håndter meldinger sendt fra pdfjs-vindu.
 * Se sfannotering/src/windowMessageSender.ts i lederplan-pdfjs prosjektet.
 */
export class PdfjsMeldingshåndtering {

    static regNavn = "PdfjsMeldingshåndtering";

    static forInjisering() {
        return [
            regNavn.ng.$rootScope,
            regNavn.ng.$log,
            PdfjsMeldingshåndtering
        ]
    }

    private _meldinger = new Subject<PdfjsMelding>();

    constructor(private $rootScope: IScope, private $log: ILogService) {
        this.registrerPdfJsMeldingslytter();
    }

    get meldinger(): Subject<PdfjsMelding> {
        return this._meldinger;
    }

    /** registrerer en global meldingslytter som ved pdfjs.lukk kaller angitt metode */
    registrerGlobalVedLukkMeldingshåndtering(vedMelding: (melding: PdfjsMelding) => void) {
        this.registrerLytter('pdfjs.lukk', melding => true, this.$rootScope, vedMelding);
    }

    /** registrerer en global meldingslytter som ved pdfjs.aapneINative kaller angitt metode */
    registrerGlobalVedÅpneINativeMeldingshåndtering(vedMelding: (melding: PdfjsMelding) => void) {
        this.registrerLytter('pdfjs.aapneINative', melding => true, this.$rootScope, vedMelding);
    }

    registrerGlobalVedFeilICordovaMeldingshåndtering(vedMelding: (melding: PdfjsMelding) => void) {
        this.registrerLytter('pdfjs.log.error', melding => melding.meldingStatus === 'cordova', this.$rootScope, vedMelding);
    }

    vedÅpneINyttVinduMelding(dokumentversjonId: string, $scope: IScope, vedMelding: (melding: PdfjsMelding) => void) {
        this.registrerLytter('pdfjs.aapneINyttVindu', melding => melding.dokumentversjonId === dokumentversjonId, $scope, vedMelding);
    }

    vedÅpneINativeMelding(dokumentversjonId: string, $scope: IScope, vedMelding: (melding: PdfjsMelding) => void) {
        this.registrerLytter('pdfjs.aapneINative', melding => melding.dokumentversjonId === dokumentversjonId, $scope, vedMelding);
    }

    vedLukkMelding(dokumentversjonId: string, $scope: IScope, vedMelding: (melding: PdfjsMelding) => void) {
        this.registrerLytter('pdfjs.lukk', melding => melding.dokumentversjonId === dokumentversjonId, $scope, vedMelding);
    }


    //hjelpemetoder

    private registrerPdfJsMeldingslytter() {
        window.addEventListener("message", melding => this.håndterMelding(melding), false);
    }

    private håndterMelding(melding: MessageEvent) {
        debug('håndter message event', melding);
        //mottar mange ulike typer meldinger - håndter kun pdfjs melding
        const type = _.get(melding, 'data.type', null);
        if (!_.startsWith(type, 'pdfjs.')) {
            debug('ignorerer message event', melding);
            return;
        }
        switch (type) {
            case 'pdfjs.log.unhandledrejection':
            case 'pdfjs.log.error':
                const logmelding = `pdfjs ${type}: ${melding.data['melding']}`;
                console.error(logmelding);
                this.$log.error(logmelding);

                // Hvis koden kjører i app ønsker vi egen håndtering for å unngå å vise web-spesifikk melding
                if (window['cordova']) {
                    if (logmelding.includes("meldingStatus: cordova")) {
                        // meldingStatus er propagert fra egen kode i lederplan-pdfjs
                        // PDF feilet, pdf-viseren ble antakaligvis aldri vist, vi propagerer videre til håndtering av appkode
                        this.broadcastMelding(type, {type: type, dokumentversjonId: null, kilde: <Window>melding.source, meldingStatus: "cordova"});
                    }
                }

                break;
            case 'pdfjs.log':
                console.log(`pdfjs: ${melding.data['melding']}`);
                break;
            default:
                const dokumentversjonId = melding.data['dokumentversjonId'];
                this.broadcastMelding(type, {type: type, dokumentversjonId: dokumentversjonId, kilde: <Window>melding.source});
        }
    }

    private broadcastMelding(eventId: string, melding: PdfjsMelding) {
        this._meldinger.next(melding); //rxjs har et WebSocketSubject - kanskje dette kunne vært brukt nå ajs er utfaset
        this.$rootScope.$apply(() => this.$rootScope.$broadcast(eventId, melding));
    }

    private registrerLytter(event: string, filter: (melding: PdfjsMelding) => boolean, scope: IScope, vedMelding: (melding: PdfjsMelding) => void) {
        scope.$on(event, (event: IAngularEvent, melding: PdfjsMelding) => {
            debug("PdfjsMelding", event, filter(melding), melding)
            if (!filter(melding)) return;
            vedMelding(melding);
        })
    }
}

export interface PdfjsMelding {
    type: Meldingstype;
    dokumentversjonId: string;
    kilde: Window;
    meldingStatus?: string;
}

export enum Meldingstype {
    lukk = 'pdfjs.lukk',
    error = 'pdfjs.log.error',
    åpneINyttVindu = 'pdfjs.aapneINyttVindu',
    åpneINative = 'pdfjs.aapneINative',
}
